import { useEffect, useState, useMemo } from "react"
import type { DragEndEvent, DragStartEvent, DragOverEvent } from "@dnd-kit/core"
import { DndContext, DragOverlay, pointerWithin } from "@dnd-kit/core"
import { default as Card } from "./weekly-objectives-card"
import { default as Column } from "./weekly-objectives-column"
import type {
  WeeklyObjective,
  WeeklyOfWeekly,
} from "../../entities/weekly-objectives/weekly-objectives"
import { useWeeklyObjectivesStore } from "../../stores/use-weekly-objectives-store"
import useWeeklyObjectives from "../../queries/use-get-weekly-objetives"
import useUpdateWeeklyObjective from "../../queries/use-update-weekly-objective"
import { msToSeconds } from "../../utils/time"
import { isBefore, isToday, differenceInSeconds, isWithinInterval, toDate } from "date-fns"
import type { Course } from "@/modules/courses/entities/courses"
import { useProductTour } from "@/modules/product-tour/stores/use-product-tour"
import { useAuth } from "@/modules/auth/store"
import type { UserEvent } from "@/services/api/cronos/user/events/get-all"

function msToHour(ms: number): number {
  const hours = ms / (1000 * 60 * 60)
  return Math.round(hours)
}

const getTotalSecondsOfEvents = (events: UserEvent[]): number => {
  const totalSeconds = events.reduce((total, event) => {
    const startDate = new Date(event.startDate)
    const endDate = new Date(event.endDate)
    const eventDurationInSeconds = differenceInSeconds(endDate, startDate)
    return total + eventDurationInSeconds
  }, 0)

  return totalSeconds
}

interface WeeklyObjectivesBoardProps {
  isDragging: boolean
  setIsDragging: (dragging: boolean) => void
  displayObjective: (column: WeeklyOfWeekly, id: string) => () => void
  activeCourses: Course[]
  userEvents: UserEvent[]
}

export default function WeeklyObjectivesBoard({
  isDragging,
  setIsDragging,
  displayObjective,
  activeCourses,
  userEvents,
}: WeeklyObjectivesBoardProps) {
  const { currentStep, setCurrentStep } = useProductTour()
  const { user } = useAuth()
  const getWeeklyObjectivesQuery = useWeeklyObjectives()
  const updateWeeklyObjective = useUpdateWeeklyObjective()
  const { setWeeklyObjectives } = useWeeklyObjectivesStore()
  const [weeklyObjective, setWeeklyObjective] = useState<WeeklyObjective | null>(null)
  const [activeColumn, setActiveColumn] = useState<string | null>(null)
  const [sourceColumn, setSourceColumn] = useState<WeeklyOfWeekly | null>(null)
  const isSkippedOrCompleted = user.productTour === "skipped" || user.productTour === "completed"

  useEffect(() => {
    if (getWeeklyObjectivesQuery.status === "success") {
      setWeeklyObjectives(getWeeklyObjectivesQuery.data.weeks)
    }
  }, [getWeeklyObjectivesQuery.status])

  const filteredColumns = useMemo(() => {
    if (!getWeeklyObjectivesQuery.data?.weeks) return []
    return getWeeklyObjectivesQuery.data.weeks.map((week, index) => {
      const objectives = week.objectives.filter((objective) =>
        activeCourses.some((c) => c.subjectId === objective.subjectId),
      )
      const totalSeconds = objectives.reduce(
        (sum, obj) => sum + msToSeconds(obj.estimatedRemainingDuration),
        0,
      )

      const realDurationInHs = objectives.reduce((sum, obj) => sum + msToHour(obj.realDuration), 0)

      // Only calculate overload for current and next week (as per acceptance criteria)
      const today = new Date()
      const weekStartDate = new Date(week.startDate)
      const weekEndDate = new Date(week.endDate)

      const isCurrentWeek = isWithinInterval(today, {
        start: toDate(week.startDate),
        end: toDate(week.endDate),
      })

      // Find current week index to determine next week
      const currentWeekIndex = getWeeklyObjectivesQuery.data.weeks.findIndex((w) =>
        isWithinInterval(today, {
          start: toDate(w.startDate),
          end: toDate(w.endDate),
        }),
      )

      const isNextWeek = index === currentWeekIndex + 1
      const shouldCalculateOverload = isCurrentWeek || isNextWeek

      let isOverloaded = false
      let availableStudyTimeInSeconds = 0

      if (shouldCalculateOverload) {
        const weekEvents = userEvents.filter((event) => {
          const eventDate = new Date(event.startDate)
          return eventDate >= weekStartDate && eventDate <= weekEndDate
        })

        availableStudyTimeInSeconds = getTotalSecondsOfEvents(weekEvents)

        // Check if the week is overloaded (remaining work > available study time)
        isOverloaded = totalSeconds > availableStudyTimeInSeconds
      }

      return {
        ...week,
        objectives,
        totalSeconds,
        realDurationInHs,
        isOverloaded,
        availableStudyTimeInSeconds,
      }
    })
  }, [getWeeklyObjectivesQuery.data?.weeks, activeCourses, userEvents])

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    const objective = filteredColumns
      .flatMap((column) => column.objectives)
      .find((task) => task.id === active.id)

    if (objective) {
      setWeeklyObjective(objective)
      const sourceColumn = filteredColumns.find((col) =>
        col.objectives.some((t) => t.id === active.id),
      )
      if (sourceColumn) {
        setActiveColumn(sourceColumn.id)
        setSourceColumn(sourceColumn)
      }
    }

    setIsDragging(true)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event
    if (!over) return

    const overId = over.id.toString()

    // find the target column
    const targetColumnId = overId.includes("column-")
      ? overId.replace("column-", "")
      : filteredColumns.find((col) => col.objectives.some((objective) => objective.id === overId))
          ?.id

    if (targetColumnId) {
      setActiveColumn(targetColumnId)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || !activeColumn) {
      setWeeklyObjective(null)
      setActiveColumn(null)
      setIsDragging(false)
      return
    }

    const overId = over.id.toString()

    // find the target column
    const destinationColumnId = overId.includes("column-")
      ? overId.replace("column-", "")
      : filteredColumns.find((col) => col.objectives.some((objective) => objective.id === overId))
          ?.id || activeColumn

    const sourceColumnIndex = filteredColumns.findIndex((col) =>
      col.objectives.some((objective) => objective.id === active.id),
    )
    const destinationColumnIndex = filteredColumns.findIndex(
      (col) => col.id === destinationColumnId,
    )

    if (sourceColumnIndex === -1 || destinationColumnIndex === -1) {
      setWeeklyObjective(null)
      setActiveColumn(null)
      setIsDragging(false)
      return
    }

    const destinationColumnStartDate = new Date(filteredColumns[destinationColumnIndex].startDate)
    const destinationColumnEndDate = new Date(filteredColumns[destinationColumnIndex].endDate)
    const today = new Date()

    const isAnOldColumn =
      isBefore(destinationColumnStartDate, today) &&
      isBefore(destinationColumnEndDate, today) &&
      !isToday(destinationColumnStartDate) &&
      !isToday(destinationColumnEndDate)

    if (isAnOldColumn) {
      setWeeklyObjective(null)
      setActiveColumn(null)
      setIsDragging(false)
      return
    }

    // Only allow moving between columns, not within the same column
    if (sourceColumnIndex !== destinationColumnIndex) {
      const sourceColumn = filteredColumns[sourceColumnIndex]
      const objectiveIndex = sourceColumn.objectives.findIndex((obj) => obj.id === active.id)
      if (objectiveIndex === -1) {
        setWeeklyObjective(null)
        setActiveColumn(null)
        setIsDragging(false)
        return
      }

      const objective = sourceColumn.objectives[objectiveIndex]
      const newDate = new Date(objective.weekDateTime)
      newDate.setDate(newDate.getDate() + (destinationColumnIndex - sourceColumnIndex) * 7)

      setIsDragging(false)
      updateWeeklyObjective.mutate({
        id: objective.id,
        date: newDate.toISOString(),
      })
    }
    if (!isSkippedOrCompleted && currentStep !== 0) {
      setCurrentStep(currentStep + 1)
    }
    setWeeklyObjective(null)
    setActiveColumn(null)
    setIsDragging(false)
  }

  return (
    <DndContext
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex h-fit w-full">
        <div className="flex h-fit w-full flex-col gap-4 pr-4 md:w-fit md:flex-row">
          {filteredColumns.map((column, index) => {
            return (
              <Column
                key={column.id}
                index={index + 1}
                column={column}
                displayObjective={displayObjective}
                totalSeconds={column.totalSeconds}
                isActive={column.id.toString() === activeColumn}
                startDate={column.startDate}
                endDate={column.endDate}
                isDragging={isDragging}
                isPending={updateWeeklyObjective.isPending}
                sourceColumn={sourceColumn}
                realDurationInHs={column.realDurationInHs}
                isOverloaded={column.isOverloaded}
              />
            )
          })}
        </div>
      </div>
      <DragOverlay style={{ willChange: "transform" }}>
        {weeklyObjective ? (
          <div className="scale-105 transform opacity-90 shadow-xl">
            <Card {...weeklyObjective} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
